// The sign-in gate, and the one place that knows Clerk exists.
//
// Everything below reads this context rather than Clerk's hooks. With
// `DASHBOARD_AUTH_DISABLED=true` (local development) there is no ClerkProvider at all, and
// Clerk's `useAuth` throws outside one; both modes implement the same small interface instead.
//
// This is only the client half. The server verifies the token and checks roles.json on every
// /api/ route, whatever this file renders.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ClerkProvider, SignIn, useAuth, useUser } from '@clerk/clerk-react';
import { fetchAuthConfig } from '../api/client.ts';
import type { TokenGetter } from '../api/client.ts';
import type { AuthConfig } from '../api/types.ts';
import { Gate } from './Gate.tsx';

interface AuthValue {
  email: string | null;
  getToken: TokenGetter;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuthContext(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuthContext used outside AuthProvider');
  return value;
}

// Local development has nobody to sign in, so requests go out without a token.
const disabledValue: AuthValue = {
  email: null,
  getToken: async () => null,
  signOut: () => {},
};

// Clerk refreshes its token on a timer and re-renders when it does. The wrappers handed out here
// keep one identity for the life of the component, so nothing downstream refetches on a refresh.
function ClerkBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { user } = useUser();

  // The current Clerk functions live in a ref; the stable wrappers call through to them.
  const clerk = useRef({ getToken, signOut });
  useEffect(() => { clerk.current = { getToken, signOut }; }, [getToken, signOut]);
  const stableGetToken = useCallback<TokenGetter>(() => clerk.current.getToken(), []);
  const stableSignOut = useCallback(() => {
    void clerk.current.signOut().then(() => window.location.reload());
  }, []);

  const email = user?.primaryEmailAddress?.emailAddress ?? null;
  const value = useMemo<AuthValue>(
    () => ({ email, getToken: stableGetToken, signOut: stableSignOut }),
    [email, stableGetToken, stableSignOut],
  );

  // Held closed until Clerk has loaded, or the sign-in form flashes for a signed-in user.
  if (!isLoaded) return <Gate message="Loading…" />;
  if (!isSignedIn) {
    return (
      <Gate message="Sign in with your Cartesia account to continue.">
        <div className="gate-signin"><SignIn routing="virtual" /></div>
      </Gate>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    fetchAuthConfig().then(setConfig).catch((error: Error) => setConfigError(error.message));
  }, []);

  // A missing environment variable on Render should say so, not leave a spinner.
  if (configError) {
    return (
      <Gate>
        <div className="notice notice-error"><b>Can't start sign-in.</b><br />{configError}</div>
      </Gate>
    );
  }
  if (!config) return <Gate message="Loading…" />;
  if (!config.enabled) return <AuthContext.Provider value={disabledValue}>{children}</AuthContext.Provider>;

  return (
    <ClerkProvider publishableKey={config.publishable_key ?? ''} localization={SIGN_IN_TEXT}>
      <ClerkBridge>{children}</ClerkBridge>
    </ClerkProvider>
  );
}

// The Clerk application is shared with the outbound dashboard and Scout, so its name reads
// "GTM-Outbound-Agent". Renaming it would change their sign-in too; this names only ours.
const SIGN_IN_TEXT = {
  signIn: {
    start: {
      title: 'Sign in to the Opportunities Dashboard',
      titleCombined: 'Sign in to the Opportunities Dashboard',
      subtitle: 'Use your Cartesia Google account',
      subtitleCombined: 'Use your Cartesia Google account',
    },
  },
};
